package com.tregz.tremenz.ui.media

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.MediaQueryList
import org.w3c.dom.events.Event
import org.w3c.dom.events.EventListener

/**
 * Counter bumped whenever the browser viewport may have moved under the overlaid players: a window
 * resize, a zoom or screen change that alters `devicePixelRatio`, or the tab becoming visible
 * again. The players convert Compose pixels with that ratio, so they must be re-placed even when
 * their Compose bounds did not change.
 */
@Composable
internal fun rememberMediaViewport(): Int {
    var tick by remember { mutableStateOf(0) }

    DisposableEffect(Unit) {
        val bump = EventListener { tick += 1 }

        // A resolution query only matches the ratio it was built for, so it is rebuilt after each
        // change to keep catching the next one.
        var densityQuery: MediaQueryList? = null
        lateinit var densityListener: EventListener
        fun watchDensity() {
            densityQuery?.removeEventListener("change", densityListener)
            densityQuery = window.matchMedia("(resolution: ${window.devicePixelRatio}dppx)").also {
                it.addEventListener("change", densityListener)
            }
        }
        densityListener = object : EventListener {
            override fun handleEvent(event: Event) {
                tick += 1
                watchDensity()
            }
        }
        watchDensity()

        // Hidden tabs throttle frames, so a resize made meanwhile may not have been laid out yet.
        val visibilityListener = EventListener {
            if (document.asDynamic().visibilityState == "visible") tick += 1
        }

        window.addEventListener("resize", bump)
        document.addEventListener("visibilitychange", visibilityListener)

        onDispose {
            window.removeEventListener("resize", bump)
            document.removeEventListener("visibilitychange", visibilityListener)
            densityQuery?.removeEventListener("change", densityListener)
        }
    }

    return tick
}
